// src/components/Modals/ManageWidgetsModal.jsx
import React, { useEffect, useMemo, useState } from "react";

/** Registry for friendly labels/descriptions */
const WIDGET_REGISTRY = [
  { key: "CostPlanner",     label: "Cost Planner",   desc: "Plan costs, margins & pricing" },
  { key: "MaterialDetails", label: "Materials",      desc: "Quick access to inventory items" },
  { key: "ReorderPO",       label: "Reorder & POs",  desc: "Low stock & purchase orders" },
  { key: "Utilities",       label: "Utilities",      desc: "See monthly/weekly/yearly costs" },
  { key: "Staff",           label: "Staff",          desc: "Team availability & quick edit" },
  { key: "StaffCost",       label: "Staff Costs",    desc: "Salaries / mo and per-unit" },
  { key: "KilnTracker",     label: "Kiln Tracker",   desc: "Recent firings at a glance" },
  { key: "Packaging",       label: "Packaging",      desc: "Library & rules overview" },
  { key: "Marketing",       label: "Marketing",      desc: "Campaign costs" },
  { key: "Templates",       label: "Templates",      desc: "Saved calculators/templates" },
  { key: "Tasks",           label: "Tasks",          desc: "Quick todo list" },
  { key: "DailyTodo",       label: "Daily To-Do",    desc: "Today’s checklist linked to Planner" },
  { key: "Workshops",       label: "Workshops",      desc: "Next workshop & quick create" },
  { key: "Production",      label: "Production",     desc: "Batches status" },
  { key: "Analytics",       label: "Analytics",      desc: "High-level numbers" },
  { key: "Participants",    label: "Participants",   desc: "Track workshop pieces & status" },
  { key: "Receipts",        label: "Receipts",       desc: "Scan & manage receipts" },

  // New widgets
  { key: "Journal",         label: "Journal",        desc: "Ideas & notes (handwritten style)" },
  { key: "MarketsChecklist",label: "Markets Checklist", desc: "Prep & pack lists with templates" },
  { key: "Streak",          label: "Streak",         desc: "Login streak & milestones" },

  // Studio booking (kiln, wheels, benches)
  { key: "StudioSpaces",    label: "Studio Booking", desc: "Book kiln, wheels & workbenches" },

  // Global studio map
  { key: "MudoMap",         label: "Mudo Map",       desc: "Global studio directory & discovery" },
];
/** Locked widgets (always present, pinned first in this order) */
const LOCKED_ORDER = [
  "CostPlanner",
  "MudoMap",
  "Streak",
  "Journal", 
  "DailyTodo",
  "Tasks",
  "Analytics",
  "MaterialDetails",
  "Staff",
  "Utilities",
  "Packaging",
  "Marketing",
  
];
const LOCKED = new Set(LOCKED_ORDER);

/**
 * Props:
 * - isOpen: boolean
 * - widgets: string[]  (CUSTOM widgets ONLY; Dashboard will add locked itself)
 * - updateWidgets: (customOnly: string[]) => void
 * - onClose: () => void
 */
export default function ManageWidgetsModal({ isOpen, widgets = [], updateWidgets, onClose }) {
  const [enabled, setEnabled] = useState(new Set());
  const [order, setOrder] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    const incomingCustom = Array.isArray(widgets) ? widgets.filter((k) => !LOCKED.has(k)) : [];
    setEnabled(new Set([...LOCKED_ORDER, ...incomingCustom]));
    setOrder(incomingCustom);
  }, [isOpen, widgets]);

  const list = useMemo(() => {
    const byKey = new Map(WIDGET_REGISTRY.map((w) => [w.key, w]));
    const lockedCards = LOCKED_ORDER
      .map((k) => byKey.get(k))
      .filter(Boolean)
      .map((w) => ({ ...w, locked: true }));

    const enabledNonLocked = order
      .map((k) => byKey.get(k))
      .filter(Boolean)
      .map((w) => ({ ...w, locked: false }));

    const accounted = new Set([...LOCKED_ORDER, ...order]);
    const remainingDisabled = WIDGET_REGISTRY
      .filter((w) => !accounted.has(w.key))
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((w) => ({ ...w, locked: false }));

    return [...lockedCards, ...enabledNonLocked, ...remainingDisabled];
  }, [order]);

  const toggle = (key) => {
    if (LOCKED.has(key)) return;
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setOrder((o) => o.filter((k) => k !== key));
      } else {
        next.add(key);
        setOrder((o) => (o.includes(key) ? o : [...o, key]));
      }
      return next;
    });
  };

  const move = (key, dir) => {
    if (LOCKED.has(key)) return;
    const idx = order.indexOf(key);
    if (idx < 0) return;
    const to = dir === "up" ? idx - 1 : idx + 1;
    if (to < 0 || to >= order.length) return;
    const copy = [...order];
    [copy[idx], copy[to]] = [copy[to], copy[idx]];
    setOrder(copy);
  };

  const save = () => {
    const customOnly = order.filter((k) => !LOCKED.has(k));
    if (typeof updateWidgets === "function") updateWidgets([...customOnly]);

    try {
      const raw = localStorage.getItem("mudoUserSettings");
      const profile = raw ? JSON.parse(raw) : {};
      profile.widgets = [...customOnly];
      localStorage.setItem("mudoUserSettings", JSON.stringify(profile));
    } catch {}

    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[2500] bg-black/60 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl rounded-xl bg-zinc-900 text-white shadow-2xl border border-white/10 overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Manage Dashboard Widgets</h3>
          <button onClick={onClose} className="text-sm leading-none" title="Close">Close</button>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-auto">
          {list.map((w) => {
            const isOn = w.locked || enabled.has(w.key);
            const pos = w.locked ? LOCKED_ORDER.indexOf(w.key) : order.indexOf(w.key);

            return (
              <div
                key={w.key}
                className={`rounded-lg p-3 border transition ${
                  isOn ? "border-white/20 bg-white/5" : "border-white/10 bg-transparent"
                }`}
                title={w.locked ? "This widget is required and cannot be removed" : ""}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{w.label}</div>
                    <div className="text-xs opacity-70">{w.desc}</div>
                  </div>

                  <div className="flex items-center gap-2">
                    {w.locked && (
                      <span className="px-2 py-1 rounded text-[10px] bg-white/10">Locked</span>
                    )}
                    <button
                      onClick={() => toggle(w.key)}
                      disabled={!!w.locked}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        isOn ? "bg-emerald-600 text-white" : "bg-zinc-800 text-zinc-200"
                      } ${w.locked ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {isOn ? "Enabled" : "Disabled"}
                    </button>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => move(w.key, "up")}
                    disabled={!!w.locked || pos <= 0}
                    className="px-2 py-1 rounded border border-white/10 disabled:opacity-40"
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => move(w.key, "down")}
                    disabled={!!w.locked || pos === -1 || pos === order.length - 1}
                    className="px-2 py-1 rounded border border-white/10 disabled:opacity-40"
                    title="Move down"
                  >
                    ↓
                  </button>
                  <div className="text-xs opacity-70 ml-auto">
                    {w.locked ? `Pinned` : pos >= 0 ? `Position: ${pos + 1}` : "—"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded border border-white/10 hover:bg-white/5">
            Cancel
          </button>
          <button
            onClick={save}
            className="px-4 py-2 rounded bg-orange-600 hover:bg-orange-700 text-white font-medium"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
